import { useState, useEffect, useRef, useCallback } from 'react';

interface Position {
  x: number;
  y: number;
}

const STORAGE_KEY = 'floatingCallPanel.position';
// Default is (0, 0) because bottom-6 left-6 provide the initial offset
const DEFAULT_POSITION = { x: 0, y: 0 };

const getInitialPosition = (): Position => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (error) {
    console.error('Failed to load panel position:', error);
  }
  return DEFAULT_POSITION;
};

const savePosition = (position: Position): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(position));
  } catch (error) {
    console.error('Failed to save panel position:', error);
  }
};

export const useDraggable = () => {
  const [isDragging, setIsDragging] = useState(false);
  const elementRef = useRef<HTMLDivElement>(null);
  const positionRef = useRef<Position>(getInitialPosition());
  const dragStartRef = useRef<Position>({ x: 0, y: 0 });

  // Apply transform to element
  const applyTransform = useCallback((x: number, y: number) => {
    if (elementRef.current) {
      elementRef.current.style.transform = `translate(${x}px, ${y}px)`;
      positionRef.current = { x, y };
    }
  }, []);

  // Set initial position on mount
  useEffect(() => {
    if (!elementRef.current) return;

    // Default position: (0, 0) because bottom-6 left-6 already provide the offset
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) {
      positionRef.current = {
        x: 0,
        y: 0,
      };
    }

    const pos = positionRef.current;
    applyTransform(pos.x, pos.y);
  }, [applyTransform]);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (!elementRef.current) return;

    // Only allow dragging when clicking on the drag handle
    const target = e.target as HTMLElement;
    if (!target.closest('[data-drag-handle="true"]')) {
      return;
    }

    e.preventDefault();

    const rect = elementRef.current.getBoundingClientRect();

    // Calculate offset from mouse position to element's corners
    // For bottom-6 left-6 positioning, we work with bottom coordinates
    dragStartRef.current = {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    };

    setIsDragging(true);
  }, []);

  useEffect(() => {
    if (!isDragging || !elementRef.current) return;

    // Cache dimensions once at the start of drag
    const rect = elementRef.current.getBoundingClientRect();
    const offset = 24; // 6 * 4px in Tailwind
    const maxX = window.innerWidth - rect.width - offset;
    const minY = -(window.innerHeight - rect.height - offset);

    const handleMouseMove = (e: MouseEvent) => {
      // Calculate new top-left position based on mouse movement
      const newX = e.clientX - dragStartRef.current.x;
      const newTopY = e.clientY - dragStartRef.current.y;

      // Convert to bottom-based Y coordinate
      const newBottomY = window.innerHeight - newTopY - rect.height;
      const newY = -newBottomY;

      // Constrain to window bounds
      const constrainedX = Math.max(-offset, Math.min(newX, maxX));
      const constrainedY = Math.max(minY, Math.min(newY, offset));

      // Direct immediate DOM manipulation - no delay
      applyTransform(constrainedX, constrainedY);
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      savePosition(positionRef.current);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, applyTransform]);

  return {
    ref: elementRef,
    isDragging,
    handleMouseDown,
  };
};
